import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, lazy, Suspense } from "react";

// Analytics
import { initAnalytics, trackPageView } from "@/services/analytics";

// SEO Hooks
import useCanonicalURL, { useRobotsReset, useFAQSchemaDedupe, useEnvironmentRobots } from "@/hooks/useSEO";

// Layout Components
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import LiveChatWidget from "@/components/common/LiveChatWidget";
import TawkWidget from "@/components/common/TawkWidget";
import JsonLdPreview from "@/components/dev/JsonLdPreview";

// Main Page Components
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import HotJobsPage from "@/pages/HotJobsPage";
import ContactPage from "@/pages/ContactPage";
import FAQPage from "@/pages/FAQPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import NewsletterPage from "@/pages/NewsletterPage";
import ThankYouPage from "@/pages/ThankYouPage";
import CareersPage from "@/pages/CareersPage";
import WhyZafcoPage from "@/pages/WhyZafcoPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import CalculatorsPage from "@/pages/CalculatorsPage";

// Heavy calculator pages — code-split via React.lazy for faster initial load + better Core Web Vitals
const CanadaCRSCalculatorPage = lazy(() => import("@/pages/CanadaCRSCalculatorPage"));
const AustraliaPointsCalculatorPageNew = lazy(() => import("@/pages/AustraliaPointsCalculatorPageNew"));
const UKPointsCalculatorPageNew = lazy(() => import("@/pages/UKPointsCalculatorPageNew"));
const GermanyChancenkarteCalculatorPage = lazy(() => import("@/pages/GermanyChancenkarteCalculatorPage"));
const QuebecPointsCalculatorPage = lazy(() => import("@/pages/QuebecPointsCalculatorPage"));
const SINPPointsCalculatorPageFinal = lazy(() => import("@/pages/SINPPointsCalculatorPageFinal"));

// Lightweight fallback rendered while a code-split chunk loads
const CalculatorChunkFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-slate-50">
    <div className="flex items-center gap-3 text-slate-600">
      <div className="w-5 h-5 border-2 border-slate-300 border-t-zafco-blue rounded-full animate-spin" />
      <span className="font-medium">Loading calculator…</span>
    </div>
  </div>
);

// Country Pages
import CanadaPRPage from "@/pages/countries/CanadaPRPage";
import AustraliaPRPage from "@/pages/countries/AustraliaPRPage";
import USAEb5Page from "@/pages/countries/USAEb5Page";
import UKSkilledPage from "@/pages/countries/UKSkilledPage";
import GermanyOpportunityPage from "@/pages/countries/GermanyOpportunityPage";
import UAEGoldenPage from "@/pages/countries/UAEGoldenPage";
import NewZealandPRPage from "@/pages/countries/NewZealandPRPage";

// Service Pages
import ResumeWritingPage from "@/pages/services/ResumeWritingPage";
import VisaPreAssessmentPage from "@/pages/services/VisaPreAssessmentPage";

// Office Pages
import OfficePage from "@/pages/offices/OfficePage";

// Legal Pages
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsPage from "@/pages/legal/TermsPage";
import DisclaimerPage from "@/pages/legal/DisclaimerPage";
import RefundPolicyPage from "@/pages/legal/RefundPolicyPage";

// Dynamic Page Template
import DynamicPage from "@/pages/DynamicPage";
import CMSPage from "@/pages/CMSPage";
import VisaCMSPage from "@/pages/VisaCMSPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { PAGE_CONTENT } from "@/data/pageContent";

// Legacy URL Redirect Components
import { CatchAllRedirect } from "@/components/common/LegacyRedirect";
import { usePHPRedirect, PHPRedirect } from "@/components/common/LegacyPHPRedirect";

// Saudi Arabia Pages
import SaudiArabiaPage from "@/pages/SaudiArabiaPage";
import SaudiBusinessVisitVisaPage from "@/pages/SaudiBusinessVisitVisaPage";
import SaudiTouristVisaPage from "@/pages/SaudiTouristVisaPage";
import SaudiWorkVisaPage from "@/pages/SaudiWorkVisaPage";
import SaudiVisaFrameworkHubPage from "@/pages/saudi-framework/SaudiVisaFrameworkHubPage";
import SaudiWorkVisaQVPPage from "@/pages/saudi-framework/SaudiWorkVisaQVPPage";
import SaudiBusinessVisitPremiumPage from "@/pages/saudi-framework/SaudiBusinessVisitPremiumPage";
import SaudiGratuityCalculatorPage from "@/pages/saudi-framework/SaudiGratuityCalculatorPage";
import InternationalManpowerRecruitmentPage from "@/pages/InternationalManpowerRecruitmentPage";
import RussiaRecruitmentAgencyIndiaPage from "@/pages/RussiaRecruitmentAgencyIndiaPage";
import PolandRecruitmentAgencyIndiaPage from "@/pages/PolandRecruitmentAgencyIndiaPage";
import EasternEuropeRecruitmentIndiaPage from "@/pages/EasternEuropeRecruitmentIndiaPage";
import CanadaHubPage from "@/pages/countries/CanadaHubPage";
import AustraliaHubPage from "@/pages/countries/AustraliaHubPage";
import SaudiVisaPage from "@/pages/SaudiVisaPage";

// Admin Pages
// Admin Pages — code-split via React.lazy (Feb 2026 Core Web Vitals fix).
// Admin routes are NEVER hit by public traffic, so loading these ~8 modules
// eagerly added unnecessary kB to every public page's initial JS bundle.
// Lazy import drops them out of the main chunk and into a separate
// /static/js/admin.[hash].chunk.js that loads only when /admin/* is visited.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));
const AdminPageEditor = lazy(() => import("@/pages/admin/AdminPageEditor"));
const AdminScoringRules = lazy(() => import("@/pages/admin/AdminScoringRules"));
const AdminPolicyUpdates = lazy(() => import("@/pages/admin/AdminPolicyUpdates"));
const AdminSEOHealth = lazy(() => import("@/pages/admin/AdminSEOHealth"));

// Study Abroad Vertical (Isolated)
import StudyAbroadRouter from "@/pages/study-abroad/StudyAbroadRouter";

// Saudi Arabia Recruitment Silo — lazy-loaded.
// Single dispatcher serves the country trunk + 5 hubs + 20 vocations
// (26 routes). Lazy import keeps this ~30 kB bundle out of the public chunk.
const JobsRoot = lazy(() => import("@/pages/jobs/JobsRoot"));

// Helper component to render dynamic pages
const DynamicPageWrapper = ({ slug }) => {
  const pageData = PAGE_CONTENT[slug];
  return <DynamicPage pageData={pageData} />;
};

// /blog/<slug> -> /blogs/<slug> permanent redirect (canonical consolidation for GSC).
// Cloudflare Bulk Redirects (Layer 3) handles this at the edge in production;
// this React-level redirect preserves SPA navigation + slug.
const BlogSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/blogs/${slug}`} replace />;
};

// Legacy bare /comparison/:slug links → canonical /study-abroad/comparison/:slug pages.
// Query strings (?source=... attribution) are preserved on every hop.
const ComparisonSlugRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/study-abroad/comparison/${slug}/${search}`} replace />;
};

// Bare /comparison directory hit → Study Abroad hub (single hop, query preserved).
const ComparisonBareRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/study-abroad/${search}`} replace />;
};

function App() {
  // Initialize analytics on app load
  useEffect(() => {
    initAnalytics();
  }, []);

  // Environment-aware robots directive.
  // On any non-production host (preview / staging / *.emergent.host /
  // *.preview.emergentagent.com / localhost / dev / temp URLs) this injects
  // a sitewide `<meta name="robots" content="noindex, nofollow">` into <head>
  // so search engines NEVER index those builds. Production domains
  // (`zafco.in`, `www.zafco.in`) stay fully crawlable & indexable.
  // Required for Google Ads "Compromised Site" remediation.
  useEnvironmentRobots();

  return (
    <div className="App min-h-screen bg-white">
      <BrowserRouter>
        <Routes>
          {/* ============================================================ */}
          {/* ADMIN ROUTES (No Header/Footer)                              */}
          {/* Lazy-loaded — chunk loads only when /admin/* is visited.     */}
          {/* ============================================================ */}
          <Route path="/admin/login" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminLogin /></Suspense>} />
          <Route path="/admin/dashboard" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminDashboard /></Suspense>} />
          <Route path="/admin/testimonials" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminTestimonials /></Suspense>} />
          <Route path="/admin/contacts" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminContacts /></Suspense>} />
          <Route path="/admin/pages" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminPages /></Suspense>} />
          <Route path="/admin/pages/new" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminPageEditor /></Suspense>} />
          <Route path="/admin/pages/edit/:pageId" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminPageEditor /></Suspense>} />
          <Route path="/admin/scoring-rules" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminScoringRules /></Suspense>} />
          <Route path="/admin/policy-updates" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminPolicyUpdates /></Suspense>} />
          <Route path="/admin/seo-health" element={<Suspense fallback={<CalculatorChunkFallback />}><AdminSEOHealth /></Suspense>} />

          {/* ============================================================ */}
          {/* STUDY ABROAD VERTICAL (Isolated — own header/footer) */}
          {/* ============================================================ */}
          {/* Legacy URL: /study-abroad/contact-us/ → /contact (consolidates SEO juice & Local Business schema) */}
          <Route
            path="/study-abroad/contact-us"
            element={<Navigate to="/contact" replace />}
          />
          <Route
            path="/study-abroad/contact-us/*"
            element={<Navigate to="/contact" replace />}
          />
          <Route path="/study-abroad/*" element={<StudyAbroadRouter />} />

          {/* ============================================================ */}
          {/* PUBLIC ROUTES (With Header/Footer) */}
          {/* ============================================================ */}
          <Route path="/*" element={<PublicLayout />} />
        </Routes>
        <Toaster position="top-right" richColors />
        {/* SEO debug widget — only renders when ?debug=schema is in the URL. Mounted at app root so it covers Admin / Study Abroad / Public layouts uniformly. */}
        <JsonLdPreview />
      </BrowserRouter>
    </div>
  );
}

// Public layout with Header and Footer
function PublicLayout() {
  // Apply canonical URL for all public pages
  useCanonicalURL();

  // SEO safety net — strip any stray <meta name="robots"> left behind by
  // admin/404 pages so public URLs are never accidentally tagged noindex.
  useRobotsReset();

  // SEO safety net — on every route change, deduplicate FAQPage JSON-LD
  // scripts in the document head. Keeps the FIRST FAQ schema, removes any
  // duplicates that legacy inline-injecting pages may have left behind.
  // Fixes "Duplicate field FAQPage" GSC Rich Results errors (Feb 2026).
  useFAQSchemaDedupe();

  // Handle legacy .php URL redirects globally
  usePHPRedirect();
  
  return (
    <>
      <Header />
      <main>
        <Routes>
          {/* ============================================================ */}
          {/* MAIN PAGES */}
          {/* ============================================================ */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />

            {/* ============================================================ */}
            {/* OVERSEAS JOBS SILO  (Master Hub + Saudi Country Silo)         */}
            {/* Route hierarchy:                                              */}
            {/*   /jobs                 → Master Jobs Hub (/jobs/JobsRoot)    */}
            {/*   /jobs/saudi-arabia-recruitment                              */}
            {/*   /jobs/{hub}-jobs-saudi-arabia            (5)                */}
            {/*   /jobs/{vocation}-jobs-saudi-arabia       (20)               */}
            {/* JobsRoot internally dispatches to SaudiJobsRouter for         */}
            {/* slug-based child URLs.                                        */}
            {/* ============================================================ */}
            <Route
              path="/jobs/*"
              element={
                <Suspense fallback={<CalculatorChunkFallback />}>
                  <JobsRoot />
                </Suspense>
              }
            />
            {/* Legacy URL redirects — preserve external backlinks */}
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/about-5" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq-page" element={<FAQPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletters" element={<NewsletterPage />} />
            <Route path="/thankyou" element={<ThankYouPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/why-zafco" element={<WhyZafcoPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/success-stories" element={<TestimonialsPage />} />
            <Route path="/calculators" element={<CalculatorsPage />} />
            <Route path="/points-calculator" element={<CalculatorsPage />} />
            <Route path="/crs-calculator" element={<CalculatorsPage />} />
            
            {/* ============================================================ */}
            {/* DEDICATED CALCULATOR PAGES - SEO OPTIMIZED (code-split via React.lazy) */}
            {/* ============================================================ */}
            <Route path="/canada-crs-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><CanadaCRSCalculatorPage /></Suspense>} />
            <Route path="/canada-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><CanadaCRSCalculatorPage /></Suspense>} />
            <Route path="/express-entry-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><CanadaCRSCalculatorPage /></Suspense>} />
            <Route path="/quebec-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><QuebecPointsCalculatorPage /></Suspense>} />
            <Route path="/quebec-skilled-worker-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><QuebecPointsCalculatorPage /></Suspense>} />
            <Route path="/australia-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><AustraliaPointsCalculatorPageNew /></Suspense>} />
            <Route path="/australia-pr-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><AustraliaPointsCalculatorPageNew /></Suspense>} />
            <Route path="/uk-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><UKPointsCalculatorPageNew /></Suspense>} />
            <Route path="/uk-skilled-worker-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><UKPointsCalculatorPageNew /></Suspense>} />
            <Route path="/sinp-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><SINPPointsCalculatorPageFinal /></Suspense>} />
            <Route path="/saskatchewan-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><SINPPointsCalculatorPageFinal /></Suspense>} />
            <Route path="/germany-chancenkarte-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><GermanyChancenkarteCalculatorPage /></Suspense>} />
            <Route path="/germany-opportunity-card-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><GermanyChancenkarteCalculatorPage /></Suspense>} />
            <Route path="/germany-points-calculator" element={<Suspense fallback={<CalculatorChunkFallback />}><GermanyChancenkarteCalculatorPage /></Suspense>} />
            
            {/* ============================================================ */}
            {/* BLOG ROUTES */}
            {/* ============================================================ */}
            <Route path="/blogs" element={<BlogIndexPage />} />
            <Route path="/blogs/:slug" element={<BlogPostPage />} />
            {/* /blog → /blogs canonical consolidation (GSC "Discovered, not indexed" fix) */}
            <Route path="/blog" element={<Navigate to="/blogs" replace />} />
            <Route path="/blog/:slug" element={<BlogSlugRedirect />} />
            
            {/* ============================================================ */}
            {/* SERVICE PAGES */}
            {/* ============================================================ */}
            <Route path="/resume-writing-services" element={<ResumeWritingPage />} />
            <Route path="/visa-pre-assesment" element={<VisaPreAssessmentPage />} />
            <Route path="/post-landing-assesment" element={<VisaPreAssessmentPage />} />

            {/* ============================================================ */}
            {/* OFFICE PAGES */}
            {/* ============================================================ */}
            <Route path="/offices/new-delhi-office" element={<OfficePage officeId="new-delhi-office" />} />
            <Route path="/offices/mumbai-office" element={<OfficePage officeId="mumbai-office" />} />
            <Route path="/offices/bangalore-office" element={<OfficePage officeId="bangalore-office" />} />
            <Route path="/offices/hyderabad-office" element={<OfficePage officeId="hyderabad-office" />} />
            <Route path="/offices/offices-in-india" element={<OfficePage officeId="new-delhi-office" />} />
            
            {/* ============================================================ */}
            {/* LEGAL PAGES */}
            {/* ============================================================ */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />

            {/* ============================================================ */}
            {/* CANADA - SKILLED MIGRATION PAGES */}
            {/* ============================================================ */}
            {/* New canonical hub + child URL */}
            <Route path="/countries/canada" element={<CanadaHubPage />} />
            <Route path="/countries/canada/canada-pr-visa" element={<CanadaPRPage />} />
            {/* Australia hub (additive — does not replace existing /skilled/australia/* routes) */}
            <Route path="/countries/australia" element={<AustraliaHubPage />} />
            <Route path="/australia" element={<Navigate to="/countries/australia" replace />} />
            {/* 301 client-side fallback — true edge 301 served via public/_redirects */}
            <Route path="/skilled/canada/canada-pr-visa" element={<Navigate to="/countries/canada/canada-pr-visa" replace />} />
            <Route path="/skilled/canada/canada-express-entry" element={<CanadaPRPage />} />
            <Route path="/skilled/canada/canada-immigration" element={<DynamicPageWrapper slug="canada-immigration" />} />
            <Route path="/immigrate-to-canada" element={<CanadaPRPage />} />
            <Route path="/skilled/canada/canada-points-calculator-for-permanent-residency" element={<DynamicPageWrapper slug="canada-points-calculator-for-permanent-residency" />} />
            <Route path="/skilled/canada/nursing-jobs-in-canada" element={<DynamicPageWrapper slug="nursing-jobs-in-canada" />} />
            <Route path="/skilled/canada/nova-scotia-nominee-program-nsnp-your-connection-to-canadian-immigration" element={<DynamicPageWrapper slug="nova-scotia-nominee-program-nsnp" />} />
            <Route path="/skilled/canada/requirements-canada-immigration-visa" element={<DynamicPageWrapper slug="requirements-canada-immigration-visa" />} />
            <Route path="/skilled/canada/occupation-list-of-canada" element={<DynamicPageWrapper slug="occupation-list-of-canada" />} />
            <Route path="/skilled/canada/how-to-immigrate-to-canada" element={<DynamicPageWrapper slug="how-to-immigrate-to-canada" />} />
            <Route path="/skilled/canada/comprehensive-ranking-system" element={<DynamicPageWrapper slug="comprehensive-ranking-system" />} />
            <Route path="/skilled/canada/latest-draw-express-entry-program" element={<DynamicPageWrapper slug="latest-draw-express-entry-program" />} />
            <Route path="/skilled/canada/minimum-ielts-score-for-canada" element={<DynamicPageWrapper slug="minimum-ielts-score-for-canada" />} />
            <Route path="/skilled/canada/average-salary-in-canada" element={<DynamicPageWrapper slug="average-salary-in-canada" />} />
            <Route path="/skilled/canada/canada-spouse-visa" element={<DynamicPageWrapper slug="canada-spouse-visa" />} />
            <Route path="/skilled/canada/canada-family-visa" element={<DynamicPageWrapper slug="canada-family-visa" />} />
            <Route path="/skilled/canada/canada-parents-visa" element={<DynamicPageWrapper slug="canada-parents-visa" />} />
            <Route path="/skilled/canada/canada-pnp-skilled-workers" element={<DynamicPageWrapper slug="canada-pnp-skilled-workers" />} />
            <Route path="/skilled/canada/ontario-pnp" element={<DynamicPageWrapper slug="ontario-pnp" />} />
            <Route path="/skilled/canada/alberta-pnp" element={<DynamicPageWrapper slug="alberta-pnp" />} />
            <Route path="/skilled/canada/british-columbia-pnp-program" element={<DynamicPageWrapper slug="british-columbia-pnp-program" />} />
            <Route path="/skilled/canada/jobs-in-canada-for-indians" element={<DynamicPageWrapper slug="jobs-in-canada-for-indians" />} />
            <Route path="/skilled/canada/quebec-skilled-worker-program" element={<DynamicPageWrapper slug="quebec-skilled-worker-program" />} />
            <Route path="/skilled/canada/canada-pr-points-system" element={<DynamicPageWrapper slug="comprehensive-ranking-system" />} />
            <Route path="/skilled/canada/application-process-and-waiting-period" element={<DynamicPageWrapper slug="application-process-and-waiting-period" />} />
            <Route path="/skilled/canada/on-line-ielts-coaching" element={<DynamicPageWrapper slug="on-line-ielts-coaching" />} />
            <Route path="/skilled/canada/canada-fsw-program-petition-process" element={<DynamicPageWrapper slug="canada-fsw-program-petition-process" />} />
            <Route path="/skilled/canada/saskatchewan-pnp" element={<DynamicPageWrapper slug="saskatchewan-pnp" />} />
            <Route path="/skilled/canada/canada-occupation-list" element={<DynamicPageWrapper slug="canada-occupation-list" />} />
            <Route path="/skilled/canada/alberta-occupation-in-demand-list" element={<DynamicPageWrapper slug="alberta-occupation-in-demand-list" />} />
            <Route path="/skilled/canada/nova-scotia-occupation-in-demand-list" element={<DynamicPageWrapper slug="nova-scotia-occupation-in-demand-list" />} />
            <Route path="/skilled/canada/manitoba-occupation-in-demand-list-of-canada" element={<DynamicPageWrapper slug="manitoba-occupation-in-demand-list-of-canada" />} />
            <Route path="/skilled/canada/quebec-occupation-in-demand-list" element={<DynamicPageWrapper slug="quebec-occupation-in-demand-list" />} />
            <Route path="/skilled/canada/manitoba-provincial-nomination-for-skilled-professionals" element={<DynamicPageWrapper slug="manitoba-provincial-nomination-for-skilled-professionals" />} />
            <Route path="/skilled/canada/authorized-representative-canada" element={<DynamicPageWrapper slug="authorized-representative-canada" />} />
            <Route path="/skilled/canada/quebec-counselling-tip" element={<DynamicPageWrapper slug="quebec-counselling-tip" />} />
            <Route path="/skilled/canada/quebec-immigration-application-process" element={<DynamicPageWrapper slug="quebec-immigration-application-process" />} />
            <Route path="/skilled/canada/benefits-of-canada-immigration" element={<DynamicPageWrapper slug="benefits-of-canada-immigration" />} />
            
            {/* ============================================================ */}
            {/* CANADA - BUSINESS IMMIGRATION */}
            {/* ============================================================ */}
            <Route path="/business/canada/canada-business-visa" element={<DynamicPageWrapper slug="canada-business-visa" />} />
            <Route path="/business/canada/canada-startup-visa" element={<DynamicPageWrapper slug="canada-startup-visa" />} />
            <Route path="/business/canada/canada-entrepreneur-visa" element={<DynamicPageWrapper slug="canada-entrepreneur-visa" />} />
            <Route path="/business/canada/canada-c-11-visa" element={<DynamicPageWrapper slug="canada-c-11-visa" />} />
            <Route path="/business/canada/canada-business-visa-faqs" element={<DynamicPageWrapper slug="canada-business-visa-faqs" />} />
            
            {/* ============================================================ */}
            {/* CANADA - VISIT/WORK/STUDY */}
            {/* ============================================================ */}
            <Route path="/visit/canada/canada-visit-visa" element={<DynamicPageWrapper slug="canada-visit-visa" />} />
            <Route path="/work/canada/canada-work-visa" element={<DynamicPageWrapper slug="canada-work-visa" />} />
            {/* Removed page — permanent redirect to Study Abroad Canada hub (SEO-safe) */}
            <Route path="/study/canada/canada-study-visa" element={<Navigate to="/study-abroad/canada/" replace />} />

            {/* ============================================================ */}
            {/* AUSTRALIA - SKILLED MIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/skilled/australia/australia-pr-visa" element={<Navigate to="/cms/australia-pr-visa" replace />} />
            <Route path="/immigrate-to-australia" element={<Navigate to="/cms/australia-pr-visa" replace />} />
            <Route path="/skilled/australia/occupation-list" element={<DynamicPageWrapper slug="occupation-list" />} />
            <Route path="/skilled/australia/australia-immigration" element={<DynamicPageWrapper slug="australia-immigration" />} />
            <Route path="/skilled/australia/australia-skilled-independent-visa-189" element={<DynamicPageWrapper slug="australia-skilled-independent-visa-189" />} />
            <Route path="/skilled/australia/australia-skilled-nominated-visa-190" element={<DynamicPageWrapper slug="australia-skilled-nominated-visa-190" />} />
            <Route path="/skilled/australia/australia-pr-points-calculator" element={<DynamicPageWrapper slug="australia-pr-points-calculator" />} />
            <Route path="/skilled/australia/australia-point-system" element={<DynamicPageWrapper slug="australia-point-system" />} />
            <Route path="/skilled/australia/australia-pr-for-nurses" element={<DynamicPageWrapper slug="australia-pr-for-nurses" />} />
            <Route path="/skilled/australia/how-to-immigrate-to-australia" element={<DynamicPageWrapper slug="how-to-immigrate-to-australia" />} />
            <Route path="/skilled/australia/australian-immigration-requirements" element={<DynamicPageWrapper slug="australian-immigration-requirements" />} />
            <Route path="/skilled/australia/benefits-of-immigration-to-australia" element={<DynamicPageWrapper slug="benefits-of-immigration-to-australia" />} />
            <Route path="/skilled/australia/nominated-or-sponsored-visa-489" element={<DynamicPageWrapper slug="nominated-or-sponsored-visa-489" />} />
            <Route path="/skilled/australia/australia-subclass-482-visa" element={<DynamicPageWrapper slug="australia-subclass-482-visa" />} />
            <Route path="/skilled/australia/family-visa-australia" element={<DynamicPageWrapper slug="family-visa-australia" />} />
            <Route path="/skilled/australia/australia-national-innovation-visa" element={<DynamicPageWrapper slug="australia-national-innovation-visa" />} />
            <Route path="/skilled/australia/settle-down-in-australia-with-the-national-innovation-visa" element={<DynamicPageWrapper slug="settle-down-in-australia-with-the-national-innovation-visa" />} />
            <Route path="/skilled/australia/australias-new-core-skills-occupation-list" element={<DynamicPageWrapper slug="australias-new-core-skills-occupation-list" />} />
            
            {/* ============================================================ */}
            {/* AUSTRALIA - STATE NOMINATION LISTS */}
            {/* ============================================================ */}
            <Route path="/skilled/australia/south-australia-state-nomination-skilled-occupation-list" element={<DynamicPageWrapper slug="south-australia-state-nomination-skilled-occupation-list" />} />
            <Route path="/skilled/australia/tasmanian-skilled-occupation-list" element={<DynamicPageWrapper slug="tasmanian-skilled-occupation-list" />} />
            <Route path="/skilled/australia/new-south-wales-occupation-list-for-subclass-190" element={<DynamicPageWrapper slug="new-south-wales-occupation-list-for-subclass-190" />} />
            <Route path="/skilled/australia/western-australian-skilled-migration-occupation-list" element={<DynamicPageWrapper slug="western-australian-skilled-migration-occupation-list" />} />
            <Route path="/skilled/australia/victoria-state-sponsorship-skilled-occupation-list" element={<DynamicPageWrapper slug="victoria-state-sponsorship-skilled-occupation-list" />} />
            <Route path="/skilled/australia/queensland-skilled-occupation-list" element={<DynamicPageWrapper slug="queensland-skilled-occupation-list" />} />
            
            {/* ============================================================ */}
            {/* AUSTRALIA - BUSINESS IMMIGRATION */}
            {/* ============================================================ */}
            <Route path="/business/australia/business-investment-temporary-subclass-188-visa" element={<DynamicPageWrapper slug="business-investment-temporary-subclass-188-visa" />} />
            <Route path="/business/australia/australia-business-subclass-888-visa" element={<DynamicPageWrapper slug="australia-business-subclass-888-visa" />} />
            <Route path="/business/australia/australia-business-subclass-132-visa" element={<DynamicPageWrapper slug="australia-business-subclass-132-visa" />} />
            <Route path="/business/australia-business/australia-business-subclass-186-visa" element={<DynamicPageWrapper slug="australia-business-subclass-186-visa" />} />
            
            {/* ============================================================ */}
            {/* AUSTRALIA - VISIT/WORK/STUDY */}
            {/* ============================================================ */}
            <Route path="/visit/australia/australia-visit-visa" element={<DynamicPageWrapper slug="australia-visit-visa" />} />
            <Route path="/work/australia/australia-work-visa" element={<DynamicPageWrapper slug="australia-work-visa" />} />
            <Route path="/study/australia/australia-study-visa" element={<DynamicPageWrapper slug="australia-study-visa" />} />

            {/* ============================================================ */}
            {/* USA - IMMIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/business/usa/eb-5-visa" element={<USAEb5Page />} />
            <Route path="/eb-5" element={<USAEb5Page />} />
            <Route path="/skilled/usa/us-visa" element={<DynamicPageWrapper slug="us-visa" />} />
            <Route path="/skilled/usa/average-salary-in-usa" element={<DynamicPageWrapper slug="average-salary-in-usa" />} />
            <Route path="/skilled/usa/b-2-visitor-visa" element={<DynamicPageWrapper slug="b-2-visitor-visa" />} />
            <Route path="/skilled/usa/b-1-visa" element={<DynamicPageWrapper slug="b-1-visa" />} />
            <Route path="/skilled/usa/family-based-green-card" element={<DynamicPageWrapper slug="family-based-green-card" />} />
            <Route path="/business/usa/usa-l1-visa" element={<DynamicPageWrapper slug="usa-l1-visa" />} />
            <Route path="/visit/usa/usa-visit-visa" element={<DynamicPageWrapper slug="usa-visit-visa" />} />
            <Route path="/study/usa/us-study-visa" element={<DynamicPageWrapper slug="us-study-visa" />} />
            
            {/* ============================================================ */}
            {/* USA - EB-5 DETAILED PAGES */}
            {/* ============================================================ */}
            <Route path="/business/usa-business/changes-in-policy-and-legislation-affecting-the-eb" element={<DynamicPageWrapper slug="changes-in-policy-and-legislation-affecting-the-eb" />} />
            <Route path="/business/usa-business/eb5-concurrent-filing" element={<DynamicPageWrapper slug="eb5-concurrent-filing" />} />
            <Route path="/business/usa-business/eb5-job-creation-requirements" element={<DynamicPageWrapper slug="eb5-job-creation-requirements" />} />
            <Route path="/business/usa-business/eb-5-integrity-act" element={<DynamicPageWrapper slug="eb-5-integrity-act" />} />
            <Route path="/business/usa-business/eb-5-legal-and-financial-considerations-in-eb" element={<DynamicPageWrapper slug="eb-5-legal-and-financial-considerations-in-eb" />} />
            <Route path="/business/usa-business/eb5-processing-times-and-backlogs" element={<DynamicPageWrapper slug="eb5-processing-times-and-backlogs" />} />
            <Route path="/business/usa-business/eb5-return-on-investment" element={<DynamicPageWrapper slug="eb5-return-on-investment" />} />
            <Route path="/business/usa-business/source-of-funds-requirements" element={<DynamicPageWrapper slug="source-of-funds-requirements" />} />
            <Route path="/business/usa-business/eb5-step-by-step-process" element={<DynamicPageWrapper slug="eb5-step-by-step-process" />} />
            <Route path="/business/usa-business/eb5-comparisons-with-other-investment-visas" element={<DynamicPageWrapper slug="eb5-comparisons-with-other-investment-visas" />} />
            <Route path="/business/usa-business/how-eb-5-benefits-outweigh-the-costs-and-complexity-for-h1-b-holders" element={<DynamicPageWrapper slug="how-eb-5-benefits-outweigh-the-costs-and-complexity-for-h1-b-holders" />} />
            <Route path="/business/usa-business/exit-strategies-for-eb" element={<DynamicPageWrapper slug="exit-strategies-for-eb" />} />
            <Route path="/business/usa-business/how-indian-h1b-holders-are-using-the-eb-5-program-to-settle-in-the-usa" element={<DynamicPageWrapper slug="how-indian-h1b-holders-are-using-the-eb-5-program-to-settle-in-the-usa" />} />
            <Route path="/business/usa-business/impact-of-us-political-climate-on-eb" element={<DynamicPageWrapper slug="impact-of-us-political-climate-on-eb" />} />
            <Route path="/business/usa-business/legal-and-financial-considerations-in-eb" element={<DynamicPageWrapper slug="legal-and-financial-considerations-in-eb" />} />
            <Route path="/business/usa-business/recent-changes-in-investment-thresholds-for-the-usa-eb" element={<DynamicPageWrapper slug="recent-changes-in-investment-thresholds-for-the-usa-eb" />} />
            <Route path="/business/usa-business/regional-centers-update" element={<DynamicPageWrapper slug="regional-centers-update" />} />
            <Route path="/business/usa-business/targeted-employment-areas" element={<DynamicPageWrapper slug="targeted-employment-areas" />} />
            <Route path="/business/usa-business/the-role-of-immigration-attorneys-and-advisors-in-the-eb" element={<DynamicPageWrapper slug="the-role-of-immigration-attorneys-and-advisors-in-the-eb" />} />
            <Route path="/business/usa-business/eb5-success-stories-and-case-studies-of-eb" element={<DynamicPageWrapper slug="eb5-success-stories-and-case-studies-of-eb" />} />

            {/* ============================================================ */}
            {/* UK - IMMIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/skilled/uk/uk-skilled-worker-visa" element={<UKSkilledPage />} />
            <Route path="/skilled/uk/uk-immigration" element={<DynamicPageWrapper slug="uk-immigration" />} />
            <Route path="/skilled/uk-skilled/uk-immigration" element={<DynamicPageWrapper slug="uk-immigration" />} />
            <Route path="/skilled/uk/uk-visa" element={<DynamicPageWrapper slug="uk-visa" />} />
            <Route path="/skilled/uk/how-to-immigrate-to-uk" element={<DynamicPageWrapper slug="how-to-immigrate-to-uk" />} />
            <Route path="/skilled/uk/uk-scale-up-worker-visa" element={<DynamicPageWrapper slug="uk-scale-up-worker-visa" />} />
            <Route path="/skilled/uk/uk-global-talent-visa" element={<DynamicPageWrapper slug="uk-global-talent-visa" />} />
            <Route path="/skilled/uk/uk-talent-program" element={<DynamicPageWrapper slug="uk-talent-program" />} />
            <Route path="/skilled/uk/jobs-in-the-uk-for-indians" element={<DynamicPageWrapper slug="jobs-in-the-uk-for-indians" />} />
            <Route path="/business/uk/uk-innovator-visa" element={<DynamicPageWrapper slug="uk-innovator-visa" />} />
            <Route path="/business/uk/uk-investor-visa" element={<DynamicPageWrapper slug="uk-investor-visa" />} />
            <Route path="/business/uk-business/isle-of-man-innovator-visa-programme" element={<DynamicPageWrapper slug="isle-of-man-innovator-visa-programme" />} />
            <Route path="/visit/uk/uk-visit-visa" element={<DynamicPageWrapper slug="uk-visit-visa" />} />
            <Route path="/study/uk/uk-study-visa" element={<DynamicPageWrapper slug="uk-study-visa" />} />

            {/* ============================================================ */}
            {/* GERMANY & EUROPE - IMMIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/germany-opportunity-card" element={<GermanyOpportunityPage />} />
            <Route path="/skilled/europe/germany-opportunity-card" element={<GermanyOpportunityPage />} />
            <Route path="/skilled/europe/europe-visa" element={<DynamicPageWrapper slug="europe-visa" />} />
            <Route path="/skilled/europe/germany-eu-blue-card" element={<DynamicPageWrapper slug="germany-eu-blue-card" />} />
            <Route path="/job-seeker/europe/germany-job-seeker-visa" element={<DynamicPageWrapper slug="germany-job-seeker-visa" />} />
            <Route path="/job-seeker/europe/portugal-job-seeker-visa" element={<DynamicPageWrapper slug="portugal-job-seeker-visa" />} />
            <Route path="/job-seeker/europe/netherlands-job-seeker-visa-your-complete-guide" element={<DynamicPageWrapper slug="netherlands-job-seeker-visa-your-complete-guide" />} />
            <Route path="/job-seeker/europe/austria-job-seeker-visa" element={<DynamicPageWrapper slug="austria-job-seeker-visa" />} />
            <Route path="/job-seeker/europe/sweden-job-seeker-visa" element={<DynamicPageWrapper slug="sweden-job-seeker-visa" />} />
            <Route path="/business/europe/netherlands-start-up-visa" element={<DynamicPageWrapper slug="netherlands-start-up-visa" />} />
            <Route path="/business/europe-business/hungary-golden-visa" element={<DynamicPageWrapper slug="hungary-golden-visa" />} />
            <Route path="/business/europe-business/greece-golden-visa" element={<DynamicPageWrapper slug="greece-golden-visa" />} />
            <Route path="/business/europe-business/latvia-trp-program" element={<DynamicPageWrapper slug="latvia-trp-program" />} />
            <Route path="/visit/schengen/schengen-visit-visa" element={<DynamicPageWrapper slug="schengen-visit-visa" />} />
            <Route path="/study/europe/europe-study-visa" element={<DynamicPageWrapper slug="europe-study-visa" />} />

            {/* ============================================================ */}
            {/* UAE - IMMIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/skilled/uae/uae-golden-visa" element={<UAEGoldenPage />} />

            {/* ============================================================ */}
            {/* NEW ZEALAND - IMMIGRATION PAGES */}
            {/* ============================================================ */}
            <Route path="/skilled/new-zealand/new-zealand-pr-visa" element={<NewZealandPRPage />} />
            <Route path="/skilled/new-zealand/nz-skilled-program" element={<DynamicPageWrapper slug="nz-skilled-program" />} />

            {/* ============================================================ */}
            {/* SAUDI ARABIA - VISA & RECRUITMENT PAGES */}
            {/* ============================================================ */}
            <Route path="/saudi-arabia" element={<SaudiArabiaPage />} />
            <Route path="/hot-jobs" element={<HotJobsPage />} />
            <Route path="/countries/saudi-arabia" element={<SaudiArabiaPage />} />
            {/* Specific Saudi visa pages — registered BEFORE the dynamic /:visaType
                route so React Router v6 picks the more specific match. */}
            <Route path="/saudi-arabia/tourist-visa" element={<SaudiTouristVisaPage />} />
            <Route path="/saudi-arabia/business-visa" element={<SaudiBusinessVisitVisaPage />} />
            <Route path="/saudi-arabia/work-visa" element={<SaudiWorkVisaPage />} />
            {/* Saudi Visa Framework 2026 — hub-and-spoke topical authority cluster */}
            <Route path="/saudi-visa-framework-2026" element={<SaudiVisaFrameworkHubPage />} />
            <Route path="/services/saudi-work-visa-qvp-attestation" element={<SaudiWorkVisaQVPPage />} />
            <Route path="/services/saudi-business-visit-visa" element={<SaudiBusinessVisitPremiumPage />} />
            <Route path="/resources/saudi-end-of-service-gratuity-calculator" element={<SaudiGratuityCalculatorPage />} />
            <Route path="/saudi-arabia/:visaType" element={<SaudiVisaPage />} />
            {/* Legacy URL kept as in-app fallback. Production traffic is edge-301'd
                via /public/_redirects to /saudi-arabia/business-visa (true 301).
                React Router <Navigate> only fires if the edge-redirect file is
                bypassed (dev / preview environments). */}
            <Route
              path="/saudi-business-visit-visa-for-indians"
              element={<Navigate to="/saudi-arabia/business-visa" replace />}
            />
            <Route
              path="/cms/saudi-arabia-work-visa"
              element={<Navigate to="/saudi-arabia/work-visa" replace />}
            />
            <Route
              path="/international-manpower-recruitment-europe-russia-israel-saudi-arabia"
              element={<InternationalManpowerRecruitmentPage />}
            />
            <Route
              path="/russia-recruitment-agency-india"
              element={<RussiaRecruitmentAgencyIndiaPage />}
            />
            <Route
              path="/poland-recruitment-agency-india"
              element={<PolandRecruitmentAgencyIndiaPage />}
            />
            <Route
              path="/eastern-europe-recruitment-india"
              element={<EasternEuropeRecruitmentIndiaPage />}
            />

            {/* ============================================================ */}
            {/* BLOG ARTICLE & NEWS PAGES */}
            {/* ============================================================ */}
            <Route path="/canada-allows-foreign-workers-to-start-new-jobs-while-waiting-for-a-work-permit-application" element={<DynamicPageWrapper slug="canada-allows-foreign-workers-to-start-new-jobs-while-waiting-for-a-work-permit-application" />} />
            <Route path="/how-to-apply-for-the-germany-opportunity-card" element={<DynamicPageWrapper slug="how-to-apply-for-the-germany-opportunity-card" />} />
            <Route path="/apply-for-australia-pr-heres-a-comprehensive-guide" element={<DynamicPageWrapper slug="apply-for-australia-pr-heres-a-comprehensive-guide" />} />
            <Route path="/how-to-apply-for-a-d-visa-for-germany-netherlands-and-sweden-in-2025" element={<DynamicPageWrapper slug="how-to-apply-for-a-d-visa-for-germany-netherlands-and-sweden-in-2025" />} />
            
            {/* Work-related article pages */}
            <Route path="/work/uk/tier-2-visa-uk-complete-guide" element={<BlogIndexPage />} />
            <Route path="/work/general/work-visa-guide" element={<BlogIndexPage />} />
            
            {/* Immigration article pages */}
            <Route path="/immigration/trends-2026" element={<BlogIndexPage />} />
            <Route path="/immigration/points-system-guide" element={<BlogIndexPage />} />

            {/* ============================================================ */}
            {/* PLACEHOLDER & TEST PAGES */}
            {/* ============================================================ */}
            <Route path="/home-new" element={<HomePage />} />
            <Route path="/brand-inquiry" element={<ContactPage />} />
            <Route path="/form-testing" element={<ContactPage />} />
            <Route path="/home-test" element={<HomePage />} />
            <Route path="/test-contact" element={<ContactPage />} />
            <Route path="/zoho" element={<ContactPage />} />
            <Route path="/demo" element={<HomePage />} />
            <Route path="/homepage-popup" element={<HomePage />} />
            <Route path="/holiday-list" element={<FAQPage />} />
            <Route path="/affiliate-program" element={<CareersPage />} />
            
            {/* ============================================================ */}
            {/* GSC SOFT-404 CONSOLIDATION — 301 redirects to high-authority hubs */}
            {/* (26 URLs from GSC Soft-404 report → study-abroad hubs) */}
            {/* ============================================================ */}
            {/* Root visa hub → Study Abroad home */}
            <Route path="/cms/visa" element={<Navigate to="/study-abroad/" replace />} />

            {/* Bare comparison URLs: directory hit → SA hub; slug → canonical comparison page (query preserved) */}
            <Route path="/comparison" element={<ComparisonBareRedirect />} />
            <Route path="/comparison/:slug" element={<ComparisonSlugRedirect />} />

            {/* /skilled hub indexes were never built — single-hop redirects to the topical calculators/home */}
            <Route path="/skilled" element={<Navigate to="/" replace />} />
            <Route path="/skilled/australia" element={<Navigate to="/australia-points-calculator" replace />} />
            <Route path="/skilled/canada" element={<Navigate to="/canada-crs-calculator" replace />} />

            {/* UAE / Dubai cluster → Dubai hub */}
            <Route path="/cms/uae-golden-visa" element={<Navigate to="/study-abroad/dubai/" replace />} />
            <Route path="/cms/uae-golden-visa-guide" element={<Navigate to="/study-abroad/dubai/" replace />} />
            <Route path="/cms/uae-investor-visa" element={<Navigate to="/study-abroad/dubai/" replace />} />

            {/* Australia cluster → Australia hub */}
            <Route path="/cms/australia-pr-visa" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/subclass-190-visa" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/australias-new-core-skills-occupation-list" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/south-australia-state-nomination-skilled-occupation-list" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/queensland-skilled-occupation-list" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/tasmanian-skilled-occupation-list" element={<Navigate to="/study-abroad/australia/" replace />} />
            <Route path="/cms/settle-down-in-australia-with-the-national-innovation-visa" element={<Navigate to="/study-abroad/australia/" replace />} />

            {/* Canada cluster → Canada hub */}
            <Route path="/cms/canada-caregiver-visa" element={<Navigate to="/study-abroad/canada/" replace />} />
            <Route path="/cms/canada-entrepreneur-visa" element={<Navigate to="/study-abroad/canada/" replace />} />
            <Route path="/cms/atlantic-immigration-program" element={<Navigate to="/study-abroad/canada/" replace />} />
            <Route path="/cms/requirements-canada-immigration-visa" element={<Navigate to="/study-abroad/canada/" replace />} />

            {/* Germany cluster → Germany hub */}
            <Route path="/cms/germany-immigration" element={<Navigate to="/study-abroad/germany/" replace />} />
            <Route path="/cms/germany-work-visa" element={<Navigate to="/study-abroad/germany/" replace />} />

            {/* UK cluster → UK hub */}
            <Route path="/cms/uk-graduate-visa" element={<Navigate to="/study-abroad/uk/" replace />} />

            {/* USA → home (no dedicated hub, but high historical authority) */}
            <Route path="/cms/usa-immigration" element={<Navigate to="/" replace />} />
            <Route path="/cms/l1-visa-guide" element={<Navigate to="/" replace />} />

            {/* Resume Writing → existing service page */}
            <Route path="/cms/resume-writing-services" element={<Navigate to="/resume-writing-services" replace />} />

            {/* True 404 (no relevant hub) — render 404 + noindex so Google drops them */}
            <Route path="/cms/spain-digital-nomad-visa" element={<NotFoundPage />} />
            <Route path="/cms/austria-job-seeker-visa" element={<NotFoundPage />} />
            <Route path="/cms/switzerland-work-permit" element={<NotFoundPage />} />
            <Route path="/cms/malaysia-mm2h-visa" element={<NotFoundPage />} />

            {/* ============================================================ */}
            {/* CMS DYNAMIC PAGES - Visa Pages with Professional Layout */}
            {/* ============================================================ */}
            {/* High-Priority Visa Pages - Use Professional Template */}
            <Route path="/cms/canada-express-entry" element={<VisaCMSPage />} />
            <Route path="/cms/uk-skilled-worker-visa" element={<VisaCMSPage />} />
            <Route path="/cms/germany-opportunity-card" element={<VisaCMSPage />} />
            <Route path="/cms/usa-green-card" element={<VisaCMSPage />} />

            {/* Additional Visa Pages - Professional Template */}
            <Route path="/cms/canada-pr-visa" element={<VisaCMSPage />} />
            <Route path="/cms/canada-immigration" element={<VisaCMSPage />} />
            <Route path="/cms/australia-immigration" element={<VisaCMSPage />} />
            <Route path="/cms/uk-immigration" element={<VisaCMSPage />} />
            <Route path="/cms/uae-work-visa" element={<VisaCMSPage />} />
            <Route path="/cms/new-zealand-pr-visa" element={<VisaCMSPage />} />
            
            {/* Generic CMS Pages - Standard Template */}
            <Route path="/cms/:slug" element={<CMSPage />} />
            <Route path="/pages/:slug" element={<CMSPage />} />
            
            {/* ============================================================ */}
            {/* LEGACY .PHP URL REDIRECTS - 301 to clean URLs */}
            {/* ============================================================ */}
            <Route path="/*.php" element={<PHPRedirect />} />
            
            {/* ============================================================ */}
            {/* WILDCARD ROUTES FOR CATEGORIES - Redirect to CMS */}
            {/* ============================================================ */}
            <Route path="/skilled/*" element={<CatchAllRedirect />} />
            <Route path="/business/*" element={<CatchAllRedirect />} />
            <Route path="/visit/*" element={<CatchAllRedirect />} />
            <Route path="/study/*" element={<CatchAllRedirect />} />
            <Route path="/job-seeker/*" element={<CatchAllRedirect />} />
            <Route path="/work/*" element={<CatchAllRedirect />} />
            <Route path="/immigration/*" element={<CatchAllRedirect />} />
            
            {/* ============================================================ */}
            {/* 404 CATCH-ALL — proper noindex 404 (was homepage, which generated */}
            {/* duplicate content / Soft-404s in GSC for every unmatched URL).   */}
            {/* ============================================================ */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
        <TawkWidget />
        <LiveChatWidget />
      </>
    );
}

export default App;
